// Booking Engine - Two Step Form JavaScript

(function () {
    'use strict';

    // Form Validation Library (justValidate-like)
    class FormValidator {
        constructor(formSelector) {
            this.form = typeof formSelector === 'string'
                ? document.querySelector(formSelector)
                : formSelector;
            this.rules = {};
            this.errors = {};
            this.init();
        }

        init() {
            if (!this.form) {
                console.error('Form not found');
                return;
            }
        }

        addField(fieldSelector, rules) {
            const field = typeof fieldSelector === 'string'
                ? document.querySelector(fieldSelector)
                : fieldSelector;

            if (!field) {
                console.error(`Field not found: ${fieldSelector}`);
                return this;
            }

            const fieldName = field.id || field.name || fieldSelector;
            this.rules[fieldName] = {
                field: field,
                rules: rules || [],
                errorElement: this.getErrorElement(field)
            };

            // Add event listeners
            field.addEventListener('input', () => this.validateField(fieldName));
            field.addEventListener('blur', () => this.validateField(fieldName));

            return this;
        }

        getErrorElement(field) {
            const errorId = field.id ? `${field.id}-error` : null;
            if (errorId) {
                return document.getElementById(errorId);
            }
            // Try to find error element nearby
            const formGroup = field.closest('.ht-engine-form-group');
            if (formGroup) {
                return formGroup.querySelector('.ht-engine-error-msg');
            }
            return null;
        }

        validateField(fieldName) {
            const fieldRule = this.rules[fieldName];
            if (!fieldRule) return true;

            const { field, rules, errorElement } = fieldRule;
            const value = field.value.trim();
            let isValid = true;
            let errorMessage = '';

            for (const rule of rules) {
                const result = this.checkRule(rule, value, field);
                if (!result.valid) {
                    isValid = false;
                    errorMessage = result.message || rule.message || 'Invalid value';
                    break;
                }
            }

            if (isValid) {
                this.hideError(fieldName, errorElement);
                delete this.errors[fieldName];
            } else {
                this.showError(fieldName, errorMessage, errorElement);
                this.errors[fieldName] = errorMessage;
            }

            // Update field styling
            field.classList.toggle('ht-engine-input-error', !isValid);

            // Update wrapper styling for mobile and location fields
            const wrapper = field.closest('.ht-engine-mobile-wrapper') || field.closest('.ht-engine-location-wrapper');
            if (wrapper) {
                wrapper.classList.toggle('ht-engine-wrapper-error', !isValid);
            }

            return isValid;
        }

        checkRule(rule, value, field) {
            // Required rule
            if (rule.required && !value) {
                return { valid: false, message: rule.message || 'This field is required' };
            }

            // Skip other rules if field is empty and not required
            if (!rule.required && !value) {
                return { valid: true };
            }

            // Min length rule
            if (rule.minLength && value.length < rule.minLength) {
                return { valid: false, message: rule.message || `Minimum ${rule.minLength} characters required` };
            }

            // Max length rule
            if (rule.maxLength && value.length > rule.maxLength) {
                return { valid: false, message: rule.message || `Maximum ${rule.maxLength} characters allowed` };
            }

            // Exact length rule
            if (rule.length && value.length !== rule.length) {
                return { valid: false, message: rule.message || `Must be exactly ${rule.length} characters` };
            }

            // Pattern rule
            if (rule.pattern && !rule.pattern.test(value)) {
                return { valid: false, message: rule.message || 'Invalid format' };
            }

            // Custom validation function
            if (rule.validator && typeof rule.validator === 'function') {
                const result = rule.validator(value, field);
                if (result !== true) {
                    return { valid: false, message: result || rule.message || 'Invalid value' };
                }
            }

            return { valid: true };
        }

        showError(fieldName, message, errorElement) {
            if (errorElement) {
                errorElement.textContent = message;
                errorElement.classList.add('show');
            }
        }

        hideError(fieldName, errorElement) {
            if (errorElement) {
                errorElement.classList.remove('show');
                errorElement.textContent = '';
            }
        }

        validate() {
            let isValid = true;
            this.errors = {};

            for (const fieldName in this.rules) {
                if (!this.validateField(fieldName)) {
                    isValid = false;
                }
            }

            return isValid;
        }

        isValid() {
            return Object.keys(this.errors).length === 0;
        }

        getErrors() {
            return { ...this.errors };
        }

        destroy() {
            // Remove event listeners if needed
            this.rules = {};
            this.errors = {};
        }
    }

    // API Configuration
    const API_CONFIG = {
        baseURL: 'https://api.hairoriginals.com/v1', // Set your API base URL here
        endpoints: {
            products: '/products',
            createLead: '/create-lead',
            availableSlots: '/available-slots',
            createOrder: '/create-order',
            checkOrderStatus: '/check-order-status',
        },
        timeout: 30000 // 30 seconds
    };

    // API Widget - Reusable API call handler
    const ApiWidget = {
        /**
         * Make API request
         * @param {string} endpoint - API endpoint
         * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
         * @param {object} data - Request payload
         * @param {object} options - Additional options (headers, timeout, etc.)
         * @returns {Promise} - API response promise
         */
        async request(endpoint, method = 'GET', data = null, options = {}) {
            const {
                headers = {},
                timeout = API_CONFIG.timeout,
                showLoading = true,
                loadingElement = null,
                onSuccess = null,
                onError = null
            } = options;

            const url = endpoint.startsWith('http') ? endpoint : `${API_CONFIG.baseURL}${endpoint}`;

            // Create AbortController for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            const requestOptions = {
                method: method.toUpperCase(),
                headers: {
                    'Content-Type': 'application/json',
                    ...headers
                },
                signal: controller.signal
            };

            if (data && (method.toUpperCase() === 'POST' || method.toUpperCase() === 'PUT' || method.toUpperCase() === 'PATCH')) {
                requestOptions.body = JSON.stringify(data);
            }

            try {
                // Show loading state
                if (showLoading && loadingElement) {
                    this.setLoadingState(loadingElement, true);
                }

                const response = await fetch(url, requestOptions);
                clearTimeout(timeoutId);

                // Parse response
                const contentType = response.headers.get('content-type');
                let responseData;

                if (contentType && contentType.includes('application/json')) {
                    responseData = await response.json();
                } else {
                    responseData = await response.text();
                }

                // Handle non-ok responses
                if (!response.ok) {
                    const error = {
                        status: response.status,
                        statusText: response.statusText,
                        message: responseData.message || responseData.error || 'An error occurred',
                        data: responseData
                    };

                    if (onError) {
                        onError(error);
                    } else {
                        this.handleError(error);
                    }

                    throw error;
                }

                // Success callback
                if (onSuccess) {
                    onSuccess(responseData);
                }

                return responseData;
            } catch (error) {
                clearTimeout(timeoutId);

                // Handle network errors or timeouts
                if (error.name === 'AbortError' || error.name === 'TimeoutError') {
                    const timeoutError = {
                        status: 408,
                        statusText: 'Request Timeout',
                        message: 'Request timed out. Please try again.',
                        data: null
                    };

                    if (onError) {
                        onError(timeoutError);
                    } else {
                        this.handleError(timeoutError);
                    }

                    throw timeoutError;
                }

                // Re-throw if it's already a handled error
                if (error.status) {
                    throw error;
                }

                // Handle network errors
                const networkError = {
                    status: 0,
                    statusText: 'Network Error',
                    message: error.message || 'Network error. Please check your connection.',
                    data: null
                };

                if (onError) {
                    onError(networkError);
                } else {
                    this.handleError(networkError);
                }

                throw networkError;
            } finally {
                // Hide loading state
                if (showLoading && loadingElement) {
                    this.setLoadingState(loadingElement, false);
                }
            }
        },

        /**
         * Set loading state on element
         * @param {HTMLElement} element - Element to show loading state
         * @param {boolean} isLoading - Loading state
         */
        setLoadingState(element, isLoading) {
            if (!element) return;

            if (isLoading) {
                element.disabled = true;
                element.classList.add('ht-engine-loading');
                const originalText = element.dataset.originalText || element.textContent.trim();
                element.dataset.originalText = originalText;

                // Add loading spinner or text
                if (element.querySelector('svg')) {
                    // If button has SVG, add spinner class
                    element.classList.add('ht-engine-btn-loading');
                } else {
                    element.textContent = 'Processing...';
                }
            } else {
                element.disabled = false;
                element.classList.remove('ht-engine-loading', 'ht-engine-btn-loading');
                const originalText = element.dataset.originalText;
                if (originalText) {
                    element.textContent = originalText;
                    delete element.dataset.originalText;
                }
            }
        },

        /**
         * Handle API errors
         * @param {object} error - Error object
         */
        handleError(error) {
            console.error('API Error:', error);

            // You can customize error handling here
            // For example, show toast notifications, alerts, etc.
            let errorMessage = error.message || 'An error occurred';

            // Customize error messages based on status code
            switch (error.status) {
                case 400:
                    errorMessage = 'Invalid request. Please check your input.';
                    break;
                case 401:
                    errorMessage = 'Unauthorized. Please log in again.';
                    break;
                case 403:
                    errorMessage = 'Access denied.';
                    break;
                case 404:
                    errorMessage = 'Resource not found.';
                    break;
                case 408:
                    errorMessage = 'Request timed out. Please try again.';
                    break;
                case 500:
                    errorMessage = 'Server error. Please try again later.';
                    break;
                case 0:
                    errorMessage = 'Network error. Please check your connection.';
                    break;
            }

            // Show error message (you can replace with toast notification)
            alert(errorMessage);
        },

        /**
         * POST request helper
         */
        async post(endpoint, data, options = {}) {
            return this.request(endpoint, 'POST', data, options);
        },

        /**
         * GET request helper
         */
        async get(endpoint, options = {}) {
            // Validate endpoint
            if (!endpoint) {
                throw new Error('Endpoint is required');
            }

            // Handle query parameters
            let url = endpoint.startsWith('http') ? endpoint : `${API_CONFIG.baseURL}${endpoint}`;

            if (options.params && Object.keys(options.params).length > 0) {
                const queryString = new URLSearchParams(options.params).toString();
                url += (url.includes('?') ? '&' : '?') + queryString;
            }

            return this.request(url, 'GET', null, options);
        },

        /**
         * PUT request helper
         */
        async put(endpoint, data, options = {}) {
            return this.request(endpoint, 'PUT', data, options);
        },

        /**
         * DELETE request helper
         */
        async delete(endpoint, options = {}) {
            return this.request(endpoint, 'DELETE', null, options);
        }
    };

    // State management
    const state = {
        currentStep: 1,
        selectedDate: null,
        selectedTimeSlot: null,
        selectedPaymentMethod: 'pay-now',
        formData: {},
        autocomplete: null,
        validator: null,
        leadId: null, // Store lead ID from API response
        isCreatingLead: false, // Prevent duplicate lead creation
        updateLeadTimeout: null, // Debounce for lead updates
        city: '' // Store extracted city from Google Places
    };

    // DOM Elements
    const elements = {
        step1: document.getElementById('ht-engine-step-1'),
        step2: document.getElementById('ht-engine-step-2'),
        nextBtn: document.getElementById('ht-engine-next-btn'),
        backBtn: document.getElementById('ht-engine-back-btn'),
        scheduleBtn: document.getElementById('ht-engine-schedule-btn'),
        mobileInput: document.getElementById('ht-engine-mobile'),
        mobileError: document.getElementById('ht-engine-mobile-error'),
        pincodeInput: document.getElementById('ht-engine-pincode'),
        fullnameInput: document.getElementById('ht-engine-fullname'),
        addressInput: document.getElementById('ht-engine-address'),
        locationInput: document.getElementById('ht-engine-location'),
        pickLocationBtn: document.getElementById('ht-engine-pick-location'),
        dateContainer: document.getElementById('ht-engine-date-container'),
        timeGrid: document.getElementById('ht-engine-time-grid'),
        paymentOptions: document.querySelectorAll('.ht-engine-payment-option'),
        collapseBtns: document.querySelectorAll('.ht-engine-collapse-btn'),
        concernsToggle: document.querySelector('.ht-engine-concerns-header'),
        concernsContent: document.querySelector('.ht-engine-concerns-content'),
        progressSteps: document.querySelectorAll('.ht-engine-progress-step')
    };

    // Initialize
    function init() {
        // Load lead ID from localStorage if exists
        const savedLeadId = localStorage.getItem('ht-engine-lead-id');
        if (savedLeadId) {
            state.leadId = savedLeadId;
        }

        initializeValidator();
        generateDates();
        generateTimeSlots([], false);
        setupEventListeners();
        setDefaultPaymentMethod();
        initGooglePlacesAutocomplete();
        loadProducts(); // Load products from API
    }

    // Initialize Form Validator
    function initializeValidator() {
        state.validator = new FormValidator('#ht-engine-step-1');

        // Mobile Number - Required, 10 digits
        state.validator.addField('#ht-engine-mobile', [
            {
                required: true,
                message: 'Mobile number is required'
            },
            {
                validator: (value) => {
                    const digits = value.replace(/\D/g, '');
                    if (digits.length !== 10) {
                        return 'Please enter 10-digit mobile number';
                    }
                    return true;
                }
            }
        ]);

        // PIN Code - Required, 6 digits
        state.validator.addField('#ht-engine-pincode', [
            {
                required: true,
                message: 'PIN code is required'
            },
            {
                validator: (value) => {
                    const digits = value.replace(/\D/g, '');
                    if (digits.length !== 6) {
                        return 'Please enter 6-digit PIN code';
                    }
                    return true;
                }
            }
        ]);

        // Full Name - Required, minimum 2 characters
        state.validator.addField('#ht-engine-fullname', [
            {
                required: true,
                message: 'Full name is required'
            },
            {
                minLength: 2,
                message: 'Full name must be at least 2 characters',
                validator: (value) => {
                    if (!value || value.trim().length < 2) {
                        return 'Full name must be at least 2 characters';
                    }
                    return true;
                }
            }
        ]);

        // Location - Optional
        state.validator.addField('#ht-engine-location', []);

        // Address - Required, minimum 5 characters
        state.validator.addField('#ht-engine-address', [
            {
                required: true,
                message: 'Address is required'
            },
            {
                minLength: 5,
                message: 'Address must be at least 5 characters',
                validator: (value) => {
                    if (!value || value.trim().length < 5) {
                        return 'Address must be at least 5 characters';
                    }
                    return true;
                }
            }
        ]);
    }

    // Debounce function for lead updates
    function debounceUpdateLead() {
        clearTimeout(state.updateLeadTimeout);
        state.updateLeadTimeout = setTimeout(() => {
            updateLeadIfExists();
        }, 1000); // Wait 1 second after user stops typing
    }

    // Check if phone and pincode are valid and create/update lead
    async function checkAndCreateOrUpdateLead() {
        const mobile = elements.mobileInput.value.replace(/\D/g, '');
        const pincode = elements.pincodeInput.value.replace(/\D/g, '');

        // Both fields must be valid
        if (mobile.length === 10 && pincode.length === 6) {
            // If lead ID doesn't exist, create new lead
            if (!state.leadId && !state.isCreatingLead) {
                await createLead();
            } else if (state.leadId) {
                // If lead ID exists, update it
                // await updateLeadIfExists();
            }
        }
    }

    // Create new lead
    async function createLead() {
        if (state.isCreatingLead) return;

        state.isCreatingLead = true;
        const mobile = elements.mobileInput.value.replace(/\D/g, '');
        const pincode = elements.pincodeInput.value.replace(/\D/g, '');

        const leadData = {
            phone: mobile,
            pincode: pincode,
            name: elements.fullnameInput.value.trim() || '',
            address: elements.addressInput.value.trim() || '',
            city: state.city || '',
            location: elements.locationInput.value.trim() || '',
            products: Array.from(document.querySelectorAll('.ht-engine-checkbox:checked')).map(cb => cb.value)
        };

        try {
            const response = await ApiWidget.post(
                API_CONFIG.endpoints.createLead,
                leadData,
                {
                    showLoading: false, // Don't show loading for background API call
                    onSuccess: (data) => {
                        // Save lead ID from response
                        const leadId = data.id || data.leadId || data.lead_id || data.data?.id;
                        if (leadId) {
                            state.leadId = leadId;
                            // Store in localStorage for persistence
                            localStorage.setItem('ht-engine-lead-id', leadId.toString());
                            console.log('Lead created successfully with ID:', leadId);
                        }
                    },
                    onError: (error) => {
                        console.error('Failed to create lead:', error);
                        // Don't show error to user for background API call
                    }
                }
            );

            // Handle response if not handled in callback
            if (response) {
                const leadId = response.id || response.leadId || response.lead_id || response.data?.id;
                if (leadId) {
                    state.leadId = leadId;
                    localStorage.setItem('ht-engine-lead-id', leadId.toString());
                }
            }
        } catch (error) {
            console.error('Create lead error:', error);
        } finally {
            state.isCreatingLead = false;
        }
    }

    // Update existing lead
    async function updateLeadIfExists() {
        if (!state.leadId) return;

        const mobile = elements.mobileInput.value.replace(/\D/g, '');
        const pincode = elements.pincodeInput.value.replace(/\D/g, '');

        // Only update if phone and pincode are still valid
        if (mobile.length !== 10 || pincode.length !== 6) {
            return;
        }

        const leadData = {
            phone: mobile,
            pincode: pincode,
            name: elements.fullnameInput.value.trim() || '',
            address: elements.addressInput.value.trim() || '',
            city: state.city || '',
            location: elements.locationInput.value.trim() || '',
            products: Array.from(document.querySelectorAll('.ht-engine-checkbox:checked')).map(cb => cb.value)
        };

        try {
            // Update lead using PUT/PATCH with lead ID in endpoint or body
            const endpoint = `${API_CONFIG.endpoints.createLead}/${state.leadId}`;

            await ApiWidget.put(
                endpoint,
                leadData,
                {
                    showLoading: false, // Don't show loading for background API call
                    onSuccess: (data) => {
                        console.log('Lead updated successfully:', state.leadId);
                    },
                    onError: (error) => {
                        console.error('Failed to update lead:', error);
                        // If update fails (e.g., lead not found), try creating new one
                        if (error.status === 404) {
                            state.leadId = null;
                            localStorage.removeItem('ht-engine-lead-id');
                            checkAndCreateOrUpdateLead();
                        }
                    }
                }
            );
        } catch (error) {
            console.error('Update lead error:', error);
            // If update fails, try creating new lead
            if (error.status === 404) {
                state.leadId = null;
                localStorage.removeItem('ht-engine-lead-id');
                checkAndCreateOrUpdateLead();
            }
        }
    }

    // Generate dates for the next 7 days
    function generateDates() {
        // Clear existing dates
        elements.dateContainer.innerHTML = '';

        const dates = [];
        const today = new Date();

        for (let i = 0; i < 8; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            dates.push(date);
        }

        dates.forEach((date, index) => {
            const dateOption = createDateElement(date, index === 0);
            elements.dateContainer.appendChild(dateOption);
        });

        if (dates.length > 0) {
            state.selectedDate = dates[0];
        }
    }

    // Create date element
    function createDateElement(date, isSelected = false) {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const dateOption = document.createElement('div');
        dateOption.className = `ht-engine-date-option ${isSelected ? 'selected' : ''}`;
        dateOption.dataset.date = date.toISOString();

        dateOption.innerHTML = `
            <span class="ht-engine-date-day">${dayNames[date.getDay()]}</span>
            <span class="ht-engine-date-number">${date.getDate()}</span>
            <span class="ht-engine-date-month">${monthNames[date.getMonth()]}</span>
        `;

        dateOption.addEventListener('click', () => selectDate(date, dateOption));

        return dateOption;
    }

    // Select date
    async function selectDate(date, element) {
        document.querySelectorAll('.ht-engine-date-option').forEach(opt => {
            opt.classList.remove('selected');
        });
        element.classList.add('selected');
        state.selectedDate = date;

        // Clear selected time slot when date changes
        state.selectedTimeSlot = null;
        document.querySelectorAll('.ht-engine-time-option').forEach(opt => {
            opt.classList.remove('selected');
        });

        // Check available time slots for selected date
        await checkTimeSlots(date);
    }

    // Check time slots for selected date
    async function checkTimeSlots(date) {
        const pincode = elements.pincodeInput.value.replace(/\D/g, '');

        // Validate pincode before making API call
        if (pincode.length !== 6) {
            // If pincode is not valid, still show time slots but don't check bookings
            generateTimeSlots([], false);
            return;
        }

        try {
            const dateString = date.toISOString().split('T')[0]; // Format: YYYY-MM-DD

            const response = await ApiWidget.post(
                API_CONFIG.endpoints.availableSlots,
                {
                    showLoading: false,
                    date: dateString,
                    pincode: pincode
                }
            );

            // Handle response - API returns available slots, so we need to invert the logic
            let availableSlots = [];
            if (response && response.data && response.data.timeSlots) {
                availableSlots = response.data.timeSlots;
            } else if (response && response.data && response.data.availableSlots) {
                availableSlots = response.data.availableSlots;
            } else if (response && response.timeSlots) {
                availableSlots = response.timeSlots;
            } else if (response && response.availableSlots) {
                availableSlots = response.availableSlots;
            } else if (Array.isArray(response)) {
                availableSlots = response;
            }

            // Generate time slots - mark slots as booked if they're NOT in available slots
            generateTimeSlots(availableSlots, true); // true indicates these are available slots

        } catch (error) {
            console.error('Failed to check available slots:', error);
            // On error, show all time slots as available
            generateTimeSlots([], true);
        }
    }

    // Generate time slots
    function generateTimeSlots(availableSlots = [], isAvailableList = false) {
        // Clear existing time slots
        elements.timeGrid.innerHTML = '';

        const timeSlots = [
            { time: '09 - 10 AM', hasExtraCharge: false },
            { time: '10 - 11 AM', hasExtraCharge: false },
            { time: '11 - 12 PM', hasExtraCharge: false },
            { time: '12 - 01 PM', hasExtraCharge: false },
            { time: '01 - 02 PM', hasExtraCharge: false },
            { time: '03 - 04 PM', hasExtraCharge: false },
            { time: '04 - 05 PM', hasExtraCharge: false },
            { time: '05 - 06 PM', hasExtraCharge: false },
            { time: '06 - 07 PM', hasExtraCharge: false }
        ];

        // Normalize available slots for comparison (handle different formats)
        const normalizedAvailableSlots = availableSlots.map(slot => {
            // Handle different time formats from API
            return slot.toString().trim();
        });

        timeSlots.forEach((slot, index) => {
            // Check if this slot is available or booked
            let isBooked = false;

            if (isAvailableList) {
                // If we received available slots, mark as booked if NOT in the list
                const isAvailable = normalizedAvailableSlots.some(availableSlot => {
                    const slotTime = slot.time.toLowerCase().replace(/\s+/g, ' ');
                    const availableTime = availableSlot.toLowerCase().replace(/\s+/g, ' ');
                    return slotTime === availableTime || availableTime.includes(slotTime) || slotTime.includes(availableTime);
                });
                isBooked = !isAvailable;
            } else {
                // Legacy: if we received booked slots (backward compatibility)
                isBooked = normalizedAvailableSlots.some(bookedSlot => {
                    const slotTime = slot.time.toLowerCase().replace(/\s+/g, ' ');
                    const bookedTime = bookedSlot.toLowerCase().replace(/\s+/g, ' ');
                    return slotTime === bookedTime || bookedTime.includes(slotTime) || slotTime.includes(bookedTime);
                });
            }

            // Don't auto-select here - we'll select one after all slots are created
            const timeOption = createTimeSlotElement(slot, false, isBooked);
            elements.timeGrid.appendChild(timeOption);
        });

        // Set default selected time slot if available
        const firstAvailableSlot = timeSlots.find((slot, index) => {
            let isBooked = false;

            if (isAvailableList) {
                const isAvailable = normalizedAvailableSlots.some(availableSlot => {
                    const slotTime = slot.time.toLowerCase().replace(/\s+/g, ' ');
                    const availableTime = availableSlot.toLowerCase().replace(/\s+/g, ' ');
                    return slotTime === availableTime || availableTime.includes(slotTime) || slotTime.includes(availableTime);
                });
                isBooked = !isAvailable;
            } else {
                isBooked = normalizedAvailableSlots.some(bookedSlot => {
                    const slotTime = slot.time.toLowerCase().replace(/\s+/g, ' ');
                    const bookedTime = bookedSlot.toLowerCase().replace(/\s+/g, ' ');
                    return slotTime === bookedTime || bookedTime.includes(slotTime) || slotTime.includes(bookedTime);
                });
            }

            return !isBooked;
        });

        if (firstAvailableSlot) {
            // Clear any existing selections first
            document.querySelectorAll('.ht-engine-time-option').forEach(opt => {
                opt.classList.remove('selected');
            });

            state.selectedTimeSlot = firstAvailableSlot.time;
            // Select ONLY the first available slot visually
            const firstAvailableElement = elements.timeGrid.querySelector(`[data-time="${firstAvailableSlot.time}"]`);
            if (firstAvailableElement && !firstAvailableElement.classList.contains('booked')) {
                firstAvailableElement.classList.add('selected');
            }
        } else {
            // If no available slots, clear selection
            state.selectedTimeSlot = null;
        }
    }

    // Load products from API
    async function loadProducts() {
        const productsContainer = document.getElementById('ht-engine-products-container');
        if (!productsContainer) return;

        try {
            const response = await ApiWidget.get(
                API_CONFIG.endpoints.products,
                {
                    showLoading: false,
                    onError: (error) => {
                        console.error('Failed to load products:', error);
                        productsContainer.innerHTML = '<div style="padding: 10px; color: #e74c3c;">Failed to load products. Please refresh the page.</div>';
                    }
                }
            );

            // Handle response - API might return data in different formats
            let products = [];
            if (response && response.data && response.data.products) {
                products = response.data.products;
            } else if (response && response.products) {
                products = response.products;
            } else if (Array.isArray(response)) {
                products = response;
            }

            // Clear container
            productsContainer.innerHTML = '';

            if (products.length === 0) {
                productsContainer.innerHTML = '<div style="padding: 10px; color: #999;">No products available.</div>';
                return;
            }

            // Create checkbox items for each product
            products.forEach((product, index) => {
                const checkboxItem = document.createElement('div');
                checkboxItem.className = 'ht-engine-checkbox-item';

                const checkboxId = `ht-engine-product-${product.value || index + 1}`;
                const checkboxValue = product.value || product.id || index + 1;
                const checkboxLabel = product.label || product.name || 'Product';

                checkboxItem.innerHTML = `
                    <input type="checkbox" id="${checkboxId}" class="ht-engine-checkbox" value="${checkboxValue}">
                    <label for="${checkboxId}" class="ht-engine-checkbox-label">
                        ${checkboxLabel}
                    </label>
                `;

                productsContainer.appendChild(checkboxItem);
            });

            // Use event delegation on the container to handle checkbox changes
            // This works even if products are reloaded since the listener is on the container
            productsContainer.addEventListener('change', function (e) {
                if (e.target.classList.contains('ht-engine-checkbox')) {
                    validateProducts();
                    // Update lead when products change
                    debounceUpdateLead();
                }
            });

        } catch (error) {
            console.error('Load products error:', error);
            if (productsContainer) {
                productsContainer.innerHTML = '<div style="padding: 10px; color: #e74c3c;">Failed to load products. Please refresh the page.</div>';
            }
        }
    }

    // Create time slot element
    function createTimeSlotElement(slot, isSelected = false, isBooked = false) {
        const timeOption = document.createElement('div');
        timeOption.className = `ht-engine-time-option ${isSelected ? 'selected' : ''} ${isBooked ? 'booked' : ''}`;
        timeOption.dataset.time = slot.time;
        timeOption.dataset.booked = isBooked ? 'true' : 'false';

        timeOption.innerHTML = `
            ${slot.time}
            ${slot.hasExtraCharge ? '<span class="ht-engine-time-badge">+₹50</span>' : ''}
        `;

        // Only allow clicking if not booked
        if (!isBooked) {
            timeOption.addEventListener('click', () => selectTimeSlot(slot.time, timeOption));
        } else {
            timeOption.style.cursor = 'not-allowed';
            timeOption.title = 'This time slot is already booked';
        }

        return timeOption;
    }

    // Select time slot
    function selectTimeSlot(time, element) {
        // Don't allow selection of booked slots
        if (element.classList.contains('booked')) {
            return;
        }

        document.querySelectorAll('.ht-engine-time-option').forEach(opt => {
            opt.classList.remove('selected');
        });
        element.classList.add('selected');
        state.selectedTimeSlot = time;
    }

    // Set default payment method
    function setDefaultPaymentMethod() {
        elements.paymentOptions.forEach(option => {
            if (option.dataset.payment === state.selectedPaymentMethod) {
                option.classList.add('selected');
            }
        });
    }

    // Setup event listeners
    function setupEventListeners() {
        // Next button
        elements.nextBtn.addEventListener('click', handleNextStep);

        // Back button
        elements.backBtn.addEventListener('click', handleBackStep);

        // Schedule button
        elements.scheduleBtn.addEventListener('click', handleSchedule);

        // Mobile number input formatting and lead creation
        elements.mobileInput.addEventListener('input', function () {
            this.value = this.value.replace(/\D/g, '').slice(0, 10);
            // Check if we should create/update lead
            checkAndCreateOrUpdateLead();
        });

        // PIN code input formatting and lead creation
        elements.pincodeInput.addEventListener('input', function () {
            this.value = this.value.replace(/\D/g, '').slice(0, 6);
            // Check if we should create/update lead
            checkAndCreateOrUpdateLead();
        });

        // PIN code auto-fill on click
        elements.pincodeInput.addEventListener('click', function () {
            // Trigger browser autofill if available
            if (this.value === '') {
                this.focus();
                // Try to trigger autocomplete dropdown
                this.setAttribute('autocomplete', 'postal-code');
            }
        });

        // Update lead when other fields change (with debounce)
        elements.fullnameInput.addEventListener('input', debounceUpdateLead);
        elements.addressInput.addEventListener('input', debounceUpdateLead);
        elements.locationInput.addEventListener('input', debounceUpdateLead);

        // Collapse buttons
        elements.collapseBtns.forEach(btn => {
            btn.addEventListener('click', function () {
                const section = this.closest('.ht-engine-section');
                const content = section.querySelector('.ht-engine-section-content');
                const isCollapsed = content.style.display === 'none';

                if (isCollapsed) {
                    content.style.display = 'block';
                    this.querySelector('.ht-engine-collapse-icon').textContent = '−';
                } else {
                    content.style.display = 'none';
                    this.querySelector('.ht-engine-collapse-icon').textContent = '+';
                }
            });
        });

        // Concerns toggle
        if (elements.concernsToggle) {
            elements.concernsToggle.addEventListener('click', function () {
                const isHidden = elements.concernsContent.classList.contains('hidden');
                const toggleIcon = this.querySelector('.ht-engine-toggle-icon');

                if (isHidden) {
                    elements.concernsContent.classList.remove('hidden');
                    this.classList.remove('collapsed');
                    if (toggleIcon) {
                        toggleIcon.textContent = '▲';
                    }
                } else {
                    elements.concernsContent.classList.add('hidden');
                    this.classList.add('collapsed');
                    if (toggleIcon) {
                        toggleIcon.textContent = '▼';
                    }
                }
            });
        }

        // Products validation - validate when any checkbox changes
        // Note: Event listeners for checkboxes are added in loadProducts() function
        // This ensures they're attached after products are loaded from API

        // Payment method selection
        elements.paymentOptions.forEach(option => {
            option.addEventListener('click', function () {
                elements.paymentOptions.forEach(opt => opt.classList.remove('selected'));
                this.classList.add('selected');
                state.selectedPaymentMethod = this.dataset.payment;
            });
        });

        // Pick location button
        if (elements.pickLocationBtn) {
            elements.pickLocationBtn.addEventListener('click', handlePickLocation);
        }
    }

    // Validate products checkboxes
    function validateProducts(ensureVisible = false) {
        const checkedProducts = document.querySelectorAll('.ht-engine-checkbox:checked');
        const productsError = document.getElementById('ht-engine-concerns-error');
        const productsSection = document.querySelector('.ht-engine-concerns-section');
        const productsContent = document.querySelector('.ht-engine-concerns-content');

        if (checkedProducts.length === 0) {
            // Ensure products section is visible if validation fails
            if (ensureVisible && productsContent && productsContent.classList.contains('hidden')) {
                productsContent.classList.remove('hidden');
                const toggleBtn = document.querySelector('.ht-engine-concerns-toggle');
                if (toggleBtn) {
                    toggleBtn.classList.remove('collapsed');
                    const toggleIcon = toggleBtn.querySelector('.ht-engine-toggle-icon');
                    if (toggleIcon) {
                        toggleIcon.textContent = '▲';
                    }
                }
            }

            // Show error message
            if (productsError) {
                productsError.classList.add('show');
                productsError.style.display = 'block'; // Force display
            }
            if (productsSection) {
                productsSection.classList.add('ht-engine-concerns-error');
            }
            return false;
        } else {
            // Hide error message
            if (productsError) {
                productsError.classList.remove('show');
                productsError.style.display = ''; // Remove inline style
            }
            if (productsSection) {
                productsSection.classList.remove('ht-engine-concerns-error');
            }
            return true;
        }
    }

    // Handle next step
    function handleNextStep() {
        // Validate all fields using validator
        if (!state.validator || !state.validator.validate()) {
            // Focus on first invalid field
            const firstError = Object.keys(state.validator.getErrors())[0];
            if (firstError) {
                const fieldRule = state.validator.rules[firstError];
                if (fieldRule && fieldRule.field) {
                    fieldRule.field.focus();
                    fieldRule.field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
            return;
        }

        // Validate products - at least one must be selected
        if (!validateProducts(true)) {
            // Scroll to products section
            const productsSection = document.querySelector('.ht-engine-concerns-section');
            if (productsSection) {
                productsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        // Save form data
        state.formData = {
            phone: elements.mobileInput.value.trim(),
            pincode: elements.pincodeInput.value.trim(),
            name: elements.fullnameInput.value.trim(),
            address: elements.addressInput.value.trim(),
            city: state.city || '',
            location: elements.locationInput.value.trim(),
            products: Array.from(document.querySelectorAll('.ht-engine-checkbox:checked')).map(cb => cb.value)
        };

        // Switch to step 2
        switchStep(2);
    }

    // Handle back step
    function handleBackStep() {
        switchStep(1);
    }

    // Handle schedule
    async function handleSchedule() {
        if (!state.selectedDate || !state.selectedTimeSlot) {
            alert('Please select a date and time slot');
            return;
        }

        // Validate that selected time slot is not booked
        const selectedElement = elements.timeGrid.querySelector(`[data-time="${state.selectedTimeSlot}"]`);
        if (selectedElement && selectedElement.classList.contains('booked')) {
            alert('The selected time slot is already booked. Please select another time slot.');
            return;
        }

        // Prepare final data
        const appointmentData = {
            ...state.formData,
            city: state.city || state.formData.city || '', // Ensure city is included
            date: state.selectedDate.toISOString(),
            timeSlot: state.selectedTimeSlot,
            paymentMethod: state.selectedPaymentMethod,
            leadId: state.leadId || null // Include lead ID if exists
        };

        try {
            // Call API using the reusable widget
            const response = await ApiWidget.post(
                API_CONFIG.endpoints.createOrder,
                appointmentData,
                {
                    showLoading: true,
                    loadingElement: elements.scheduleBtn,
                    onSuccess: (data) => {
                        console.log('Appointment scheduled successfully:', data);

                        // Store booking data in localStorage for thank-you page
                        localStorage.setItem('bookingData', JSON.stringify({
                            name: state.formData.name || state.formData.phone,
                            fullname: state.formData.name,
                            date: state.selectedDate.toISOString(),
                            timeSlot: state.selectedTimeSlot,
                            paymentMethod: state.selectedPaymentMethod,
                            appointmentId: data.id || data.appointmentId || null
                        }));

                        // Redirect to thank-you page
                        window.location.href = data.data.redirect_to;
                    },
                    onError: (error) => {
                        console.error('Failed to schedule appointment:', error);
                        // Error is already handled by ApiWidget.handleError()
                        // You can add custom error handling here if needed
                    }
                }
            );

            // If onSuccess doesn't redirect, handle response here
            if (response) {
                // Store booking data in localStorage for thank-you page
                localStorage.setItem('bookingData', JSON.stringify({
                    name: state.formData.name || state.formData.phone,
                    fullname: state.formData.name,
                    date: state.selectedDate.toISOString(),
                    timeSlot: state.selectedTimeSlot,
                    paymentMethod: state.selectedPaymentMethod,
                    appointmentId: response.id || response.appointmentId || null
                }));

                // Redirect to thank-you page
                // window.location.href = 'thank-you.html';
            }
        } catch (error) {
            // Error is already handled by ApiWidget, but you can add additional handling here
            console.error('Schedule appointment error:', error);
        }
    }

    // Switch step
    function switchStep(step) {
        state.currentStep = step;

        // Update step visibility
        elements.step1.classList.toggle('ht-engine-step-active', step === 1);
        elements.step2.classList.toggle('ht-engine-step-active', step === 2);

        // Update progress indicator
        elements.progressSteps.forEach((progressStep, index) => {
            if (index + 1 <= step) {
                progressStep.classList.add('ht-engine-progress-step-active');
            } else {
                progressStep.classList.remove('ht-engine-progress-step-active');
            }
        });

        // If switching to step 2 and a date is already selected, check time slots
        if (step === 2 && state.selectedDate) {
            checkTimeSlots(state.selectedDate);
        }

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Initialize Google Places Autocomplete
    function initGooglePlacesAutocomplete() {
        if (typeof google !== 'undefined' && google.maps && google.maps.places) {
            const autocomplete = new google.maps.places.Autocomplete(elements.locationInput, {
                types: ['geocode', 'establishment'],
                componentRestrictions: { country: 'in' } // Restrict to India
            });

            autocomplete.addListener('place_changed', function () {
                const place = autocomplete.getPlace();
                if (place.geometry) {
                    // You can access place details here
                    // place.formatted_address, place.geometry.location, etc.
                    console.log('Selected place:', place.formatted_address);

                    // Extract and auto-fill PIN code if available
                    const pincode = extractPincodeFromAddress(place);
                    if (pincode && pincode.length === 6) {
                        elements.pincodeInput.value = pincode;
                        // Trigger validation
                        if (state.validator) {
                            state.validator.validateField('ht-engine-pincode');
                        }
                    }

                    // Extract and store city
                    const city = extractCityFromAddress(place);
                    if (city) {
                        state.city = city;
                        // Update lead if it exists
                        debounceUpdateLead();
                    }
                }
            });

            state.autocomplete = autocomplete;
        }
    }

    // Extract PIN code from address components
    function extractPincodeFromAddress(place) {
        if (!place || !place.address_components) return null;

        for (let i = 0; i < place.address_components.length; i++) {
            const component = place.address_components[i];
            const types = component.types;

            // Check for postal_code in address components
            if (types.includes('postal_code')) {
                const pincode = component.long_name || component.short_name;
                // Extract only digits and limit to 6 digits
                const digits = pincode.replace(/\D/g, '').slice(0, 6);
                if (digits.length === 6) {
                    return digits;
                }
            }
        }
        return null;
    }

    // Extract city from address components
    function extractCityFromAddress(place) {
        if (!place || !place.address_components) return '';

        // Priority order: locality, administrative_area_level_2, administrative_area_level_1
        const cityTypes = ['locality', 'administrative_area_level_2', 'administrative_area_level_1'];

        for (const cityType of cityTypes) {
            for (let i = 0; i < place.address_components.length; i++) {
                const component = place.address_components[i];
                const types = component.types;

                if (types.includes(cityType)) {
                    const city = component.long_name || component.short_name;
                    if (city) {
                        return city.trim();
                    }
                }
            }
        }
        return '';
    }

    // Update pick location button icon
    function updatePickLocationIcon(isLoading) {
        if (isLoading) {
            // Show loading spinner
            elements.pickLocationBtn.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="ht-engine-location-loading">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="15.7" stroke-dashoffset="15.7" opacity="0.3"/>
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="15.7" stroke-dashoffset="15.7">
                        <animate attributeName="stroke-dasharray" dur="1.5s" values="0 31.4;15.7 15.7;0 31.4" repeatCount="indefinite"/>
                        <animate attributeName="stroke-dashoffset" dur="1.5s" values="0;-15.7;-31.4" repeatCount="indefinite"/>
                    </circle>
                </svg>
            `;
        } else {
            // Show location icon
            elements.pickLocationBtn.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
                </svg>
            `;
        }
    }

    // Handle pick location
    function handlePickLocation() {
        if (navigator.geolocation) {
            updatePickLocationIcon(true);
            elements.pickLocationBtn.disabled = true;

            navigator.geolocation.getCurrentPosition(
                function (position) {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;

                    // Use Google Geocoding API to get address from coordinates
                    if (typeof google !== 'undefined' && google.maps) {
                        const geocoder = new google.maps.Geocoder();
                        const latlng = { lat: lat, lng: lng };

                        geocoder.geocode({ location: latlng }, function (results, status) {
                            if (status === 'OK' && results[0]) {
                                const place = results[0];
                                elements.locationInput.value = place.formatted_address;

                                // Extract and auto-fill PIN code
                                const pincode = extractPincodeFromAddress(place);
                                if (pincode && pincode.length === 6) {
                                    elements.pincodeInput.value = pincode;
                                    // Trigger validation
                                    if (state.validator) {
                                        state.validator.validateField('ht-engine-pincode');
                                    }
                                }

                                // Extract and store city
                                const city = extractCityFromAddress(place);
                                if (city) {
                                    state.city = city;
                                    // Update lead if it exists
                                    debounceUpdateLead();
                                }
                            } else {
                                elements.locationInput.value = `${lat}, ${lng}`;
                                // Clear city if geocoding fails
                                state.city = '';
                            }
                            updatePickLocationIcon(false);
                            elements.pickLocationBtn.disabled = false;
                        });
                    } else {
                        elements.locationInput.value = `${lat}, ${lng}`;
                        updatePickLocationIcon(false);
                        elements.pickLocationBtn.disabled = false;
                    }
                },
                function (error) {
                    alert('Unable to retrieve your location. Please enter it manually.');
                    updatePickLocationIcon(false);
                    elements.pickLocationBtn.disabled = false;
                }
            );
        } else {
            alert('Geolocation is not supported by your browser. Please enter location manually.');
        }
    }

    // Reset form (optional)
    function resetForm() {
        document.querySelectorAll('.ht-engine-input').forEach(input => {
            input.value = '';
        });

        document.querySelectorAll('.ht-engine-checkbox').forEach(checkbox => {
            checkbox.checked = false;
        });

        document.querySelectorAll('.ht-engine-date-option').forEach(opt => opt.classList.remove('selected'));
        document.querySelectorAll('.ht-engine-time-option').forEach(opt => opt.classList.remove('selected'));
        elements.paymentOptions.forEach(opt => opt.classList.remove('selected'));

        state.selectedDate = null;
        state.selectedTimeSlot = null;
        state.selectedPaymentMethod = 'pay-now';
        state.formData = {};

        switchStep(1);
        generateDates();
        generateTimeSlots([], false);
        setDefaultPaymentMethod();
    }

    // Initialize on DOM load
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();

